// FCM token registration + foreground message handler.

import { fb, messaging } from '../../core/firebase.js';
import { state } from '../../core/state.js';
import { fcmTokensCol } from '../../core/paths.js';
import { toast } from '../../components/toast.js';

const { addDoc, query, where, getDocs, updateDoc, doc } = fb;

// VAPID public key for Firebase Cloud Messaging Web Push.
// Se inyecta en build via VITE_FCM_VAPID_KEY (GitHub secret / .env local).
// Obtenerla: Firebase Console → Project settings → Cloud Messaging → Web push certificates
const VAPID_KEY = import.meta.env.VITE_FCM_VAPID_KEY
  || 'BEbkTk-W7VKQpDc9eRgMNDfH8YYqfMVwTPwjkPMtv_l0RKaQpHrqlqIBb-r2eVZ4f2VbiLLwYZ6BJqHvHEEFXNc'; // placeholder

async function ensureServiceWorker() {
  if (!('serviceWorker' in navigator)) return null;
  try {
    // Scope propio: el scope '/' lo ocupa el SW de la PWA (workbox); dos SW
    // no pueden compartir scope. getToken recibe esta registration explícita.
    const reg = await navigator.serviceWorker.register('/firebase-messaging-sw.js', {
      scope: '/firebase-cloud-messaging-push-scope',
    });
    return reg;
  } catch (e) {
    console.warn('[fcm] sw register failed', e);
    return null;
  }
}

async function registerToken(role, heroId = null) {
  if (!messaging) {
    console.warn('[fcm] messaging not available');
    return null;
  }
  if (!('Notification' in window)) return null;
  if (Notification.permission === 'denied') return null;

  try {
    if (Notification.permission !== 'granted') {
      const perm = await Notification.requestPermission();
      if (perm !== 'granted') {
        console.log('[fcm] permission denied');
        return null;
      }
    }

    const reg = await ensureServiceWorker();
    if (!reg) return null;

    const token = await fb.getToken(messaging, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: reg,
    });
    if (!token) {
      console.warn('[fcm] empty token');
      return null;
    }

    // Dedup by token
    const existing = await getDocs(query(fcmTokensCol(), where('token', '==', token)));
    if (!existing.empty) {
      await updateDoc(existing.docs[0].ref, {
        role,
        heroId: heroId || null,
        updatedAt: Date.now(),
      });
    } else {
      await addDoc(fcmTokensCol(), {
        token,
        role,
        heroId: heroId || null,
        platform: /Android|iPhone|iPad/.test(navigator.userAgent) ? 'mobile' : 'web',
        userAgent: navigator.userAgent.slice(0, 200),
        createdAt: Date.now(),
        updatedAt: Date.now(),
      });
    }
    console.log('[fcm] token registered for role=' + role);
    return token;
  } catch (e) {
    console.warn('[fcm] register failed', e);
    return null;
  }
}

export async function registerCurrentUser() {
  if (!state.familyId) return null;
  if (state.isHeroMode && state.selectedHeroId) {
    return registerToken('hero', state.selectedHeroId);
  }
  return registerToken('admin');
}

export function listenForegroundMessages() {
  if (!messaging) return () => {};
  try {
    return fb.onFCMMessage(messaging, (payload) => {
      const title = payload.notification?.title || payload.data?.title || 'HeroMind';
      const body = payload.notification?.body || payload.data?.body || '';
      toast(`${title}${body ? ': ' + body : ''}`, { type: 'info', duration: 5000 });
    });
  } catch (e) {
    console.warn('[fcm] foreground listener failed', e);
    return () => {};
  }
}

/**
 * Show a permission prompt button if permission is default.
 * Returns true if user already has permission or after granting.
 */
export async function requestNotifPermissionIfNeeded() {
  if (!('Notification' in window)) return false;
  if (Notification.permission === 'granted') return true;
  if (Notification.permission === 'denied') return false;
  const result = await Notification.requestPermission();
  return result === 'granted';
}

if (typeof window !== 'undefined') {
  window.requestNotifPermissionIfNeeded = requestNotifPermissionIfNeeded;
}
